import { useState, useCallback, useEffect, useRef } from 'react'
import { aiAssistantService, ChatMessage, ChatSession } from '@/services/aiAssistantService'

export interface DocumentSource {
  id: string
  name: string
  type: string | null
}

export interface ChatMessageWithSources extends ChatMessage {
  sources?: DocumentSource[]
}

// Simple UUID generator
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const STORAGE_KEY = 'logidocs-chat-session'

export function useAIAssistant() {
  const [messages, setMessages] = useState<ChatMessageWithSources[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSearchingDocuments, setIsSearchingDocuments] = useState(false)
  const [enableRAG, setEnableRAG] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string>(() => generateId())
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const messagesRef = useRef(messages)
  messagesRef.current = messages

  // Load current session from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        if (parsed.messages && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
          setMessages(
            parsed.messages.map((m: ChatMessageWithSources) => ({
              ...m,
              timestamp: new Date(m.timestamp),
            }))
          )
          setSessionId(parsed.sessionId || generateId())
        }
      } catch {
        console.error('Error loading chat session from localStorage')
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [])

  // Save to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          sessionId,
          messages,
        })
      )
    }
  }, [messages, sessionId])

  // Load past sessions
  const loadSessions = useCallback(async () => {
    try {
      const data = await aiAssistantService.getChatSessions()
      setSessions(data)
    } catch (e) {
      console.error('Error loading sessions:', e)
    }
  }, [])

  // Send a message
  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isLoading) return

      setError(null)
      setIsLoading(true)
      setIsSearchingDocuments(false)

      const userMessage: ChatMessageWithSources = {
        id: generateId(),
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      }

      const updatedHistory = [...messagesRef.current, userMessage]
      setMessages(updatedHistory)

      try {
        const result = await aiAssistantService.sendMessage(
          content.trim(),
          updatedHistory,
          {
            enableRAG,
            onSearchingDocuments: () => setIsSearchingDocuments(true),
          }
        )

        setIsSearchingDocuments(false)

        const assistantMessage: ChatMessageWithSources = {
          id: generateId(),
          role: 'assistant',
          content: result.response,
          timestamp: new Date(),
          sources: result.sources.length > 0 ? result.sources : undefined,
        }

        setMessages(prev => [...prev, assistantMessage])

        // Save session to database (don't await, fire and forget)
        const allMessages = [...updatedHistory, assistantMessage]
        aiAssistantService.saveChatSession({
          id: sessionId,
          title: aiAssistantService.generateSessionTitle(updatedHistory[0]?.content || content),
          messages: allMessages,
          createdAt: new Date(),
          updatedAt: new Date(),
        }).catch(console.error)

      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Failed to get response'
        setError(errorMessage)
      } finally {
        setIsLoading(false)
        setIsSearchingDocuments(false)
      }
    },
    [isLoading, sessionId, enableRAG]
  )

  // Start a new chat
  const newChat = useCallback(() => {
    setMessages([])
    setSessionId(generateId())
    setError(null)
    localStorage.removeItem(STORAGE_KEY)
  }, [])

  // Load a previous session
  const loadSession = useCallback((session: ChatSession) => {
    setMessages(session.messages)
    setSessionId(session.id)
    setError(null)
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        sessionId: session.id,
        messages: session.messages,
      })
    )
  }, [])

  // Delete a session
  const deleteSession = useCallback(async (sessionIdToDelete: string) => {
    try {
      await aiAssistantService.deleteChatSession(sessionIdToDelete)
    } catch (e) {
      console.error('Failed to delete chat session:', e)
      return
    }
    if (sessionIdToDelete === sessionId) {
      newChat()
    }
    await loadSessions()
  }, [sessionId, newChat, loadSessions])

  return {
    messages,
    isLoading,
    isSearchingDocuments,
    enableRAG,
    setEnableRAG,
    error,
    sendMessage,
    newChat,
    sessions,
    loadSessions,
    loadSession,
    deleteSession,
  }
}
